// The status pill + D11 flag badges shared by the list, detail, calendar
// and print screens — one place so the colour mapping can't drift between
// them. D11: `Amended` is a flag beside the pill, never the pill itself, so
// an amended checked-in booking still reads "Checked in".
import type { BookingDisplayFlags, BookingDisplayStatus } from '@namibiago/shared/policy';
import { AlertIcon } from '@/icons';

const STATUS_STYLES: Record<BookingDisplayStatus, string> = {
  Pending: 'bg-tint-sand text-ink',
  Confirmed: 'bg-tint-sky text-ink',
  'Checked in': 'bg-tint-info text-ink',
  'Fully paid': 'bg-tint-success text-success',
  Completed: 'bg-tint-success text-success',
  'Balance outstanding': 'bg-tint-warning text-warning',
  'Balance not recorded': 'bg-tint-warning text-danger',
  'No-show': 'bg-tint-warning text-danger',
  'Cancelled·host': 'bg-surface-2 text-ink-3',
  'Cancelled·guest': 'bg-surface-2 text-ink-3',
  Expired: 'bg-surface-2 text-ink-3',
};

export function StatusPill({ status }: { status: BookingDisplayStatus | null }) {
  if (!status) {
    return (
      <span className="inline-flex items-center rounded-chip bg-surface-2 px-sm py-xs text-caption text-ink-3">
        Unknown
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center rounded-chip px-sm py-xs text-caption font-semibold ${STATUS_STYLES[status]}`}>
      {status}
    </span>
  );
}

const FLAG_LABELS: Array<{ key: keyof BookingDisplayFlags; label: string }> = [
  { key: 'amended', label: 'Amended' },
  { key: 'balance_outstanding', label: 'Balance outstanding' },
  { key: 'fee_refunded', label: 'Fee refunded' },
];

export function FlagBadges({ flags }: { flags: BookingDisplayFlags | null }) {
  if (!flags) return null;
  const active = FLAG_LABELS.filter((f) => flags[f.key]);
  if (active.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-xs">
      {active.map((f) => (
        <span
          key={f.key}
          className="inline-flex items-center gap-xs rounded-chip bg-tint-violet px-sm py-xs text-caption font-semibold text-ink"
        >
          <AlertIcon className="h-3 w-3" aria-hidden="true" />
          {f.label}
        </span>
      ))}
    </span>
  );
}
